<template>
    <BenefitsCollapsibleSection :title="t('benefitsCenter-login-title')" :default-open="false">
        <section class="benefits-login-section-block benefits-login-background-config">
            <h3>{{ t("benefitsCenter-login-background") }}</h3>

            <BenefitsSegmentedControl v-model="internalValue.background.type"
                :aria-label="t('benefitsCenter-login-background')" :options="backgroundOptions" @input="emitChange" />

            <div v-if="internalValue.background.type === 'solid'" class="benefits-login-background-field">
                <label class="benefits-login-background-label" for="benefits-login-background-color">
                    {{ t("benefitsCenter-login-color") }}
                    <span aria-hidden="true">*</span>
                </label>

                <KvColorInput
                    id="benefits-login-background-color"
                    v-model="internalValue.background.color"
                    variant="outlined"
                    size="large"
                    @input="emitChange"
                />
            </div>

            <KvImageInput
                v-else
                v-model="internalValue.background.image"
                variant="outlined"
                size="large"
                :title="t('benefitsCenter-login-backgroundImageTitle')"
                :description="t('benefitsCenter-login-backgroundImageDescription')"
                :button-label="t('benefitsCenter-login-backgroundImageButton')"
                :delete-label="t('benefitsCenter-login-deleteBackgroundImage')"
                @input="emitChange"
            />
        </section>

        <section class="benefits-login-section-block benefits-login-form-fields">
            <h3>{{ t("benefitsCenter-login-form") }}</h3>

            <div class="benefits-login-form-list">
                <span class="benefits-login-form-chip">
                    {{ t("benefitsCenter-login-firstName") }}
                </span>

                <span class="benefits-login-form-chip">
                    {{ t("benefitsCenter-login-lastName") }}
                </span>

                <span class="benefits-login-form-chip">
                    {{ t("benefitsCenter-login-birthDate") }}
                </span>

                <div class="benefits-login-form-email-row">
                    <span class="benefits-login-form-chip">
                        {{ t("benefitsCenter-login-email") }}
                    </span>

                    <label class="benefits-login-form-required">
                        <BenefitsToggle v-model="internalValue.form.emailRequired" @input="emitChange" />

                        <span>{{ t("benefitsCenter-login-required") }}</span>
                    </label>
                </div>
            </div>
        </section>
    </BenefitsCollapsibleSection>
</template>

<script>
import Icon from "../../../components/icon/icon";
import KvColorInput from "../../../components/inputs/kv-color-input.vue";
import BenefitsCollapsibleSection from "../../shared/benefits-collapsible-section.vue";
import BenefitsSegmentedControl from "../../shared/benefits-segmented-control.vue";
import KvImageInput from "../../../components/inputs/kv-image-input.vue";
import BenefitsToggle from "../../shared/benefits-toggle.vue";

export default {
    name: "BenefitsLoginSection",
    components: {
        Icon,
        KvColorInput,
        BenefitsCollapsibleSection,
        BenefitsSegmentedControl,
        KvImageInput,
        BenefitsToggle,
    },
    model: {
        prop: "value",
        event: "input",
    },
    props: {
        value: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            internalValue: this.normalizeValue(this.value),
        };
    },
    computed: {
        backgroundOptions() {
            return [
                { value: "solid", label: this.t("benefitsCenter-login-solidColor") },
                { value: "image", label: this.t("benefitsCenter-login-image") },
            ];
        },
    },
    watch: {
        value: {
            deep: true,
            handler(value) {
                this.internalValue = this.normalizeValue(value);
            },
        },
    },
    methods: {
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },
        normalizeValue(value) {
            return {
                background: {
                    type: value.background?.type || "solid",
                    color: value.background?.color || "#2aaa4c",
                    image: value.background?.image || null,
                },
                form: {
                    firstNameRequired: true,
                    lastNameRequired: true,
                    birthDateRequired: true,
                    emailRequired: value.form?.emailRequired !== false,
                },
            };
        },
        emitChange() {
            this.$nextTick(() => {
                this.$emit("input", {
                    ...this.internalValue,
                    form: {
                        ...this.internalValue.form,
                        firstNameRequired: true,
                        lastNameRequired: true,
                        birthDateRequired: true,
                    },
                });
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.benefits-login-section-block {
    flex: 0 1 50%;
    max-width: none;
    min-width: 0;
    width: 50%;
}

.benefits-login-background-config,
.benefits-login-form-fields {
    display: flex;
    flex-direction: column;
    gap: 12px;

    h3 {
        color: #384250;
        font-family: "Sora", sans-serif;
        font-size: 16px;
        font-weight: 500;
        line-height: 1.3;
        margin: 0;
    }
}

.benefits-login-background-config {
    max-width: 500px;
}

.benefits-login-background-field {
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
}

.benefits-login-background-label {
    color: #384250;
    font-family: "Sora", sans-serif;
    font-size: 16px;
    font-weight: 500;
    line-height: 1.3;
    margin: 0;

    span {
        color: #f04438;
        font-family: "Inter", sans-serif;
        font-size: 18px;
        font-weight: 400;
        line-height: 0;
    }
}

.benefits-login-form-list {
    align-items: flex-start;
    display: flex;
    flex-direction: column;
    gap: 12px;
    max-width: 100%;
}

.benefits-login-form-chip {
    align-items: center;
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    color: #384250;
    display: inline-flex;
    font-family: "Inter", sans-serif;
    font-size: 14px;
    font-weight: 400;
    justify-content: center;
    line-height: 1.2;
    padding: 8px;
    max-width: 100%;
    overflow-wrap: anywhere;
}

.benefits-login-form-email-row {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    max-width: 100%;
}

.benefits-login-form-required {
    align-items: center;
    color: #384250;
    cursor: pointer;
    display: inline-flex;
    font-family: "Inter", sans-serif;
    font-size: 14px;
    font-weight: 400;
    gap: 8px;
    line-height: 1.2;
    margin: 0;

    input {
        height: 1px;
        opacity: 0;
        position: absolute;
        width: 1px;
    }

    input:focus-visible+.benefits-login-form-checkbox {
        box-shadow: 0 0 0 2px rgba(56, 66, 80, 0.18);
    }
}

.benefits-login-form-checkbox {
    align-items: center;
    background: #fcfcfd;
    border: 1px solid #d2d6db;
    border-radius: 3px;
    display: inline-flex;
    height: 14px;
    justify-content: center;
    width: 14px;
}

.benefits-login-form-required input:checked+.benefits-login-form-checkbox {
    background: #2aaa4c;
    border-color: #2aaa4c;
}

@media (max-width: 1100px) {
    .benefits-login-section-block {
        flex-basis: 100%;
        width: 100%;
    }

    .benefits-login-background-config {
        max-width: none;
    }
}

@media (max-width: 480px) {
    .benefits-login-form-required {
        min-height: 32px;
    }
}
</style>
